
import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { Router } from "@angular/router";
import { ModalController } from "@ionic/angular";
import { firstValueFrom, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AddressValidationService, StripeLocationValidationResult } from "src/app/_services/address-validation.service";
import { AppService } from "src/app/_services/app.service";
import { AuthService } from "src/app/_services/auth.service";
import { LocalStorageService } from "src/app/_services/localStorage.service";
import { osService } from "src/app/_services/osService.service";
import { PusherService } from "src/app/_services/pusher.service";
import { SessionStorageService } from "src/app/_services/session-storage.service";
import { getCountryCodeFromPhonePrefix } from "src/app/helpers/phone-helper";
import { FirstStepData } from "src/app/models/register-state.interface";
import { ProgressiveAddressFieldsComponent } from "src/app/shared/form-components/progressive-address-fields/progressive-address-fields.component";
import { ImageFieldConfig } from "src/app/shared/form-components/image-field/image-field.component";
import { PhoneFieldComponent } from "src/app/shared/form-components/phone-field/phone-field.component";

// Fonction utilitaire pour valider uniquement pour le rôle 'prestataire'
function requiredForPrestataire(getRole: () => string): ValidatorFn {
  return (control: AbstractControl) => {
    const role = getRole();
    if (role === 'prestataire' && !control.value) {
      return { required: true };
    }
    return null;
  };
}

// Type pour les résultats de validation
interface ValidationResult {
  isValid: boolean;
  hasRequiredFieldsEmpty?: boolean;
  phoneValidationError?: boolean;
  addressValidationError?: boolean;
  formInvalid?: boolean;
}
@Component({
  selector: "app-first-step",
  templateUrl: "./first-step.page.html",
  styleUrls: ["./first-step.page.scss"],
})
export class FirstStepPage implements OnInit, OnChanges {

  @ViewChild('phoneField') phoneFieldComponent!: PhoneFieldComponent;
  @ViewChild('addressFields') addressFieldsComponent!: ProgressiveAddressFieldsComponent;

  @Input() step: any = 0;
  @Input() f: any;
  @Output() ValidForm: EventEmitter<any> = new EventEmitter<any>();
  @Output() goTo: EventEmitter<any> = new EventEmitter<any>();
  @Input() role: any;
  @Input() form: any;
  submitted = false;
  isLoading = false;
  submittedParticular = false;
  emailError = '';
  selectedCountry: any = {
    code: "Fr",
    dialCode: "+33",
    flag: "https://flagcdn.com/w320/fr.png",
    name: "Tunisia",
  };
  countriesModal: boolean = false;
  selectedCode: any = "+33";
  stringInCode: any = "";
  phoneModel: any;
  phoneModelProvider: any;
  countries: any[] = [];
  filtredCountries: any[] = [];

  imageFieldConfig: ImageFieldConfig = {
    multiple: false,
    maxFiles: 1,
    previewType: 'grid',
    previewSize: 'small',
    showFileSize: true,
    showRemoveButton: true,
    acceptTypes: ['image/*'],
    maxFileSize: 5,
    dragAndDrop: false,
    autoPreview: true
  };
  constructor(
    private fb: FormBuilder, private appService: AppService,
    private authService: AuthService,
    private router: Router,
    private osService: osService,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private modalController: ModalController,
    private pusherService: PusherService,
    private addressValidationService: AddressValidationService
  ) { }
  registerFormProvider: FormGroup = this.fb.group({
    name_fields: [""],
    email: [""],
    password_fields: [""],
    phone_field: [""],
    country_code: [""],
    code: [""],
    gender_field: ["", requiredForPrestataire(() => this.role)],
    birthdate_field: ["", requiredForPrestataire(() => this.role)],
    address_fields: ["", requiredForPrestataire(() => this.role)],
    role: [[], Validators.required],
    image: [null], // Image is not required
  });

  ngOnInit() {
    this.getCountriesInfo();

    // Restore form data first, then apply any passed data
    this.restoreFormData();

    // Apply passed form data if available (from parent component)
    if (this.f) {
      this.registerFormProvider.patchValue(this.f);
    }

    // Met à jour la validation du code postal à chaque changement de pays
    this.registerFormProvider.get('country_code')?.valueChanges.subscribe(() => {
      this.registerFormProvider.get('zip_code')?.updateValueAndValidity();
      this.registerFormProvider.get('phone')?.updateValueAndValidity();
    });

    // Effacer l'erreur d'email quand l'utilisateur modifie le champ email
    this.registerFormProvider.get('email')?.valueChanges.subscribe(() => {
      this.clearEmailError();
    });

    // Auto-save form data on changes
    this.registerFormProvider.valueChanges.subscribe(() => {
      this.autoSaveFormData();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['role'] && changes['role'].currentValue) {
      this.setRole(changes['role'].currentValue);
    }
  }

  getCountriesInfo() {
    this.osService.getCountriesWithFlag().subscribe((data: any[]) => {
      this.countries = data;
      this.filtredCountries = data;
      this.selectedCountry = data.find((country) => country.dialCode === "+33");

    });
  }
  setCountryCode(code: string) {
    this.registerFormProvider.patchValue({
      country_code: code,
    });
  }
  setCountryPhoneCode(code: string) {
    this.registerFormProvider.patchValue({
      code: code,
    });
  }

  get provider() {
    return this.registerFormProvider.controls;
  }

  async nextSteps(stepper: any) {
    this.prepareFormData();
    this.step = stepper;
    this.submitted = true;
    this.isLoading = true;

    const validationResult = await this.validateForm();
    if (!validationResult.isValid) {
      await this.handleValidationErrors(validationResult);
      this.isLoading = false;
      return;
    }

    this.checkEmailAndProceed();
  }

  checkMail(): Observable<boolean> {
    const data: any = {
      email: this.registerFormProvider.value.email,
    };

    return this.authService.checkEmailUsed(data).pipe(
      map((res: any) => res.body.exists),
      catchError((error) => {
        console.error('Error checking email:', error);
        return of(false);
      })
    );
  }
  prev(stepper: any) {
    this.step = stepper;
  }

  /**
   * Prépare les données du formulaire avant validation
   */
  private prepareFormData(): void {
    const code = this.registerFormProvider.value.code || this.selectedCode;
    const phonePrefix = code;
    const countryCode = getCountryCodeFromPhonePrefix(phonePrefix);

    this.setCountryCode(countryCode);
    this.setCountryPhoneCode(phonePrefix);
    this.registerFormProvider.patchValue({ country_code: countryCode, code });
  }

  /**
   * Valide le formulaire de manière centralisée
   */
  private async validateForm(): Promise<ValidationResult> {
    this.markAllFieldsAsTouched();

    const hasRequiredFieldsEmpty = this.checkRequiredFieldsEmpty();
    const phoneValidationError = this.checkPhoneValidation();
    const addressValidationError = await this.checkAddressValidation();
    const formInvalid = this.registerFormProvider.invalid;

    const isValid = !formInvalid && !hasRequiredFieldsEmpty && !phoneValidationError && !addressValidationError;

    return {
      isValid,
      hasRequiredFieldsEmpty,
      phoneValidationError,
      addressValidationError,
      formInvalid
    };
  }

  /**
   * Gère l'affichage des erreurs de validation
   */
  private async handleValidationErrors(result: ValidationResult): Promise<void> {
    if (result.addressValidationError) {
      // L'erreur d'adresse est déjà affichée dans checkAddressValidation
      return;
    }

    if (result.phoneValidationError) {
      await this.appService.presentToast(
        "Veuillez corriger les erreurs dans le champ téléphone",
        "danger"
      );
    } else if (result.hasRequiredFieldsEmpty) {
      await this.appService.presentToast(
        "Veuillez remplir tous les champs requis",
        "danger"
      );
    } else if (result.formInvalid) {
      await this.appService.presentToast(
        "Veuillez corriger les erreurs dans le formulaire",
        "danger"
      );
    }
  }

  /**
   * Vérifie l'email et procède à l'étape suivante si valide
   */
  private checkEmailAndProceed(): void {
    this.checkMail().subscribe({
      next: (emailExists: boolean) => {
        if (emailExists) {
          this.isLoading = false;
          this.emailError = 'Cet email est déjà utilisé. Veuillez choisir un autre email.';
        } else {
          this.emailError = '';
          this.goTo.emit(this.step);
          this.ValidForm.emit(this.registerFormProvider.value);
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Erreur lors de la vérification de l\'email:', error);
        this.isLoading = false;
        this.emailError = 'Erreur lors de la vérification de l\'email. Veuillez réessayer.';
      }
    });
  }

  markAllFieldsAsTouched() {
    Object.keys(this.registerFormProvider.controls).forEach(key => {
      const control = this.registerFormProvider.get(key);
      if (control) {
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  /**
   * Vérifie si les champs requis sont vides
   */
  checkRequiredFieldsEmpty(): boolean {
    if (this.role !== 'prestataire') {
      return false;
    }

    const requiredFields = ['gender_field', 'birthdate_field', 'address_fields'];

    return requiredFields.some(fieldName => {
      const control = this.registerFormProvider.get(fieldName);
      const value = control?.value;
      return this.isFieldEmpty(value, fieldName);
    });
  }

  /**
   * Vérifie si un champ est vide selon son type
   */
  private isFieldEmpty(value: any, fieldName: string): boolean {
    if (!value) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (typeof value === 'object') {
      if (fieldName === 'address_fields') {
        return !this.hasRequiredAddressFields(value);
      }
      return !Object.values(value).some(val =>
        val && (typeof val === 'string' ? val.trim() !== '' : true)
      );
    }

    return false;
  }

  /**
   * Vérifie la validation du champ téléphone
   */
  checkPhoneValidation(): boolean {
    const phoneField = this.registerFormProvider.get('phone_field');
    if (!phoneField) {
      return false;
    }

    phoneField.markAsTouched();
    phoneField.updateValueAndValidity();

    if (phoneField.hasError('required') || phoneField.invalid) {
      return true;
    }

    // Vérifier la validation du composant téléphone
    if (this.phoneFieldComponent) {
      this.phoneFieldComponent.submitted = true;
      const phoneControl = this.phoneFieldComponent.phoneControl;
      if (phoneControl && (phoneControl.invalid || phoneControl.hasError('required'))) {
        return true;
      }
    }

    // Vérifier la valeur du téléphone
    const phoneValue = phoneField.value;
    if (!this.role) {
      return false; // Pas de validation si pas de rôle
    }

    if (!phoneValue || phoneValue === '' || phoneValue === null || phoneValue === undefined) {
      return true;
    }

    const phoneNumber = this.extractPhoneNumber(phoneValue);
    if (!phoneNumber || phoneNumber.length < 8) {
      return true;
    }

    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    return !phoneRegex.test(phoneNumber);
  }

  /**
   * Extrait le numéro de téléphone de la valeur (objet ou string)
   */
  private extractPhoneNumber(phoneValue: any): string {
    if (typeof phoneValue === 'object' && phoneValue.phone) {
      return phoneValue.phone.trim();
    }
    if (typeof phoneValue === 'string') {
      return phoneValue.trim();
    }
    return '';
  }

  /**
   * Vérifie la validation des champs d'adresse (incluant Stripe)
   */
  async checkAddressValidation(): Promise<boolean> {
    const addressField = this.registerFormProvider.get('address_fields');
    if (!addressField) {
      return false;
    }

    addressField.markAsTouched();
    addressField.updateValueAndValidity();

    if (addressField.hasError('required') || addressField.invalid) {
      return true;
    }

    // Vérifier la validation du composant adresse
    if (this.addressFieldsComponent) {
      this.addressFieldsComponent.submitted = true;
      this.addressFieldsComponent.forceValidation();

      const { addressForm, addressControl, cityControl, zipControl } = this.addressFieldsComponent;

      if (addressForm?.invalid || addressControl?.invalid || cityControl?.invalid || zipControl?.invalid) {
        return true;
      }
    }

    // Vérifier si l'adresse est requise pour prestataire
    const addressValue = addressField.value;
    if (this.role === 'prestataire') {
      if (!addressValue || addressValue === '' ||
        (typeof addressValue === 'object' && !this.hasRequiredAddressFields(addressValue))) {
        return true;
      }
    }

    // Valider avec Stripe si l'adresse est remplie
    if (addressValue && typeof addressValue === 'object') {
      if (!this.hasRequiredAddressFields(addressValue)) {
        return true;
      }

      const stripeCountryCode = this.getStripeCountryCode();
      const isValid = await this.validateStripeLocationWithApi({
        address: addressValue.address.trim(),
        city: addressValue.city.trim(),
        zip_code: addressValue.zip_code.trim(),
        country: stripeCountryCode
      });

      return !isValid;
    }

    return false;
  }

  /**
   * Vérifie si tous les champs requis de l'adresse sont présents
   */
  private hasRequiredAddressFields(addressValue: any): boolean {
    return addressValue.address?.trim() &&
      addressValue.city?.trim() &&
      addressValue.zip_code?.trim();
  }

  /**
   * Obtient le code pays pour Stripe (2 caractères)
   */
  private getStripeCountryCode(): string {
    const countryCode = this.registerFormProvider.value.country_code ||
      this.selectedCountry?.code ||
      'FR';
    return countryCode.toUpperCase().substring(0, 2);
  }

  /**
   * Valide l'adresse avec l'API Stripe
   */
  private async validateStripeLocationWithApi(payload: {
    address: string;
    city: string;
    zip_code: string;
    country: string;
  }): Promise<boolean> {
    try {
      const result = await firstValueFrom(
        this.addressValidationService.verifyStripeLocation(payload)
      );

      if (result.isValid) {
        this.clearStripeValidationErrors();
        return true;
      }

      this.applyStripeValidationErrors(result);
      return false;
    } catch (error) {
      const errorMessage = "Erreur lors de la validation de l'adresse avec Stripe.";
      this.setStripeErrorOnControls({ message: errorMessage });
      return false;
    }
  }

  private applyStripeValidationErrors(result: StripeLocationValidationResult) {
    // Construire le message avec la suggestion si disponible
    let message = result.message || "Les informations d'adresse ne sont pas valides selon Stripe.";
    if (result.suggestion) {
      message += ` ${result.suggestion}`;
    }

    this.setStripeErrorOnControls({
      address: result.errors?.address,
      city: result.errors?.city,
      zip: result.errors?.zip_code,
      country: result.errors?.country || result.errors?.country_code,
      message
    });
  }

  private clearStripeValidationErrors() {
    if (this.addressFieldsComponent) {
      const { addressControl, cityControl, zipControl } = this.addressFieldsComponent;

      [addressControl, cityControl, zipControl].forEach(control => {
        if (control && control.hasError('stripeVerification')) {
          const errors = { ...control.errors };
          delete errors['stripeVerification'];
          control.setErrors(Object.keys(errors).length ? errors : null);
        }
      });
    }
  }

  private setStripeErrorOnControls(params: {
    address?: string;
    city?: string;
    zip?: string;
    country?: string;
    message?: string;
  }) {
    if (this.addressFieldsComponent) {
      const { addressControl, cityControl, zipControl } = this.addressFieldsComponent;

      if (addressControl && params.address) {
        addressControl.setErrors({
          ...(addressControl.errors || {}),
          stripeVerification: params.address
        });
        addressControl.markAsTouched();
      }

      if (cityControl && params.city) {
        cityControl.setErrors({
          ...(cityControl.errors || {}),
          stripeVerification: params.city
        });
        cityControl.markAsTouched();
      }

      if (zipControl && params.zip) {
        zipControl.setErrors({
          ...(zipControl.errors || {}),
          stripeVerification: params.zip
        });
        zipControl.markAsTouched();
      }
    }

    // Les erreurs sont affichées directement sur les champs, pas besoin de toast
  }

  async submitFormProvider() {
    this.submitted = true;
    this.isLoading = true;

    const validationResult = await this.validateForm();
    if (!validationResult.isValid) {
      await this.handleValidationErrors(validationResult);
      this.isLoading = false;
      return;
    }

    this.isLoading = false;
    this.ValidForm.emit(this.registerFormProvider.value);
  }

  async submitFormParticular() {
    this.submitted = true;
    this.isLoading = true;

    const validationResult = await this.validateForm();
    if (!validationResult.isValid) {
      await this.handleValidationErrors(validationResult);
      this.isLoading = false;
      return;
    }

    // Extract data from component values
    const formValue = this.registerFormProvider.value;
    const formData = this.extractFormData(formValue);

    // Vérifier si l'email existe déjà AVANT de créer l'utilisateur
    this.checkMail().subscribe({
      next: (emailExists: boolean) => {
        if (emailExists) {
          this.isLoading = false;
          this.emailError = 'Cet email est déjà utilisé. Veuillez choisir un autre email.';
        } else {
          this.emailError = '';
          this.createUser(formData);
        }
      },
      error: (error) => {
        console.error('Erreur lors de la vérification de l\'email:', error);
        this.isLoading = false;
        this.emailError = 'Erreur lors de la vérification de l\'email. Veuillez réessayer.';
      }
    });
  }

  /**
   * Extrait les données du formulaire pour la création d'utilisateur
   */
  private extractFormData(formValue: any) {
    return {
      formValue,
      phoneData: formValue.phone_field || {},
      nameData: formValue.name_fields || {},
      emailData: formValue.email || {},
      passwordData: formValue.password_fields || {},
      imageData: formValue.image?.images || null
    };
  }

  private createUser(data: ReturnType<typeof this.extractFormData>) {
    const { formValue, phoneData, nameData, emailData, passwordData, imageData } = data;
    const formData = new FormData();
    formData.append("first_name", nameData.first_name || "");
    formData.append("last_name", nameData.last_name || "");
    formData.append("password", passwordData.password || "");
    formData.append("password_confirmation", passwordData.password_confirmation || "");
    formData.append(
      "role[]",
      this.role === "prestataire" ? "prestataire" : "client"
    );
    formData.append("phone", phoneData.phone || "");
    formData.append("email", emailData.email || "");
    formData.append("statut_pro", "ACTIVE");

    if (imageData && imageData instanceof File) {
      formData.append("image", imageData);
    } else if (imageData && Array.isArray(imageData) && imageData.length > 0) {
      // Si c'est un tableau de fichiers
      imageData.forEach((file, index) => {
        if (file instanceof File) {
          formData.append(`image[${index}]`, file);
        }
      });
    }

    this.authService.createUser(formData).subscribe({
      next: (res: any) => {
        try {
          if (res.status === 201 || res.status === 200) {
            // La réponse peut être dans res.body.data ou directement dans res.body
            const response = res.body?.data || res.body;
            // Vérifications de sécurité
            if (!response) {
              console.error("Response is empty");
              this.isLoading = false;
              return;
            }

            const token = response.access_token;
            const user = response.user;
            const refresh = response.refresh_token;

            // Vérifier que les données essentielles existent
            if (!token || !user) {
              console.error("Missing token or user data", { token: !!token, user: !!user });
              this.isLoading = false;
              return;
            }

            // Traiter les rôles comme dans login.page.ts
            const processedUser = {
              ...user,
              roles: user.roles && Array.isArray(user.roles)
                ? user.roles.map((r: any) => typeof r === 'string' ? r : r.name)
                : (user.roles || [])
            };

            // Vérifier et obtenir le rôle de manière sécurisée
            const userRole = processedUser.roles && processedUser.roles.length > 0
              ? (typeof processedUser.roles[0] === 'string' ? processedUser.roles[0] : processedUser.roles[0].name)
              : this.role || 'client';


            // Sauvegarder les données d'authentification
            this.localStorageService.saveAuthDataFromLogin(
              token,
              processedUser,
              userRole,
              refresh
            );

            // Attendre un peu pour s'assurer que les données sont sauvegardées
            setTimeout(() => {
              // Appel de getCompletetion avec gestion d'erreur
              this.authService.getCompletetion(userRole).subscribe({
                next: (completionRes: any) => {
                  if (completionRes?.body?.profile_completion !== undefined) {
                    this.appService.setUserCompletion(completionRes.body.profile_completion);
                  }
                },
                error: (err) => {
                  console.error("Error getting completion:", err);
                  // Ne pas bloquer le processus si cette requête échoue
                }
              });

              // Initialisation Pusher avec gestion d'erreur
              this.pusherService.auth().subscribe({
                next: (pusherRes) => {
                  this.pusherService.subscribeToRoleChannel();
                  this.pusherService.subscribeToDiscussionChannel();
                },
                error: (err) => {
                  console.error("Error initializing Pusher:", err);
                  // Ne pas bloquer le processus si Pusher échoue
                }
              });
            }, 100);

            // Nettoyer l'état d'enregistrement
            this.clearRegisterStateAfterSuccess();

            // Naviguer vers la page appropriée selon le rôle (comme dans login)
            setTimeout(() => {
              if (userRole === "prestataire") {
                this.router.navigateByUrl("/prestataire", { replaceUrl: true });
              } else {
                this.router.navigateByUrl("/particulier", { replaceUrl: true });
              }
            }, 200);
          } else {
            console.error("Unexpected status code:", res.status);
          }
        } catch (error) {
          console.error("Error processing registration response:", error);
        } finally {
          this.isLoading = false;
        }
      },
      error: (errors: HttpErrorResponse) => {
        console.error("error : ", errors);
        if (errors.status === 422) {
          const errorsResponse = errors.error?.errors;
          if (errorsResponse?.email) {
            // Afficher l'erreur d'email du serveur
            this.emailError = errorsResponse.email[0] || 'Email invalide';
          }
        } else if (errors.status === 409) {
          // Email conflict
          this.emailError = 'Cet email est déjà utilisé.';
        }
        this.isLoading = false;
      }
    });
  }
  async submit() {
    this.isLoading = true;

    if (this.role === "prestataire") {
      await this.submitFormProvider();
    } else {
      await this.submitFormParticular();
    }
  }

  onSelectionChange(event: any) {
    this.selectedCode = event.detail.value;
  }

  goToLogin() {
    this.router.navigate(["/"]);
  }

  setRole(role: string) {
    this.registerFormProvider.patchValue({ role: [role] });
  }

  clearEmailError() {
    this.emailError = '';
  }

  /**
   * Restore form data from session storage
   */
  private restoreFormData(): void {
    const savedData = this.sessionStorageService.getRegisterFirstStep();
    if (savedData) {
      // Restore form values
      this.registerFormProvider.patchValue(savedData);

      // Restore country selection if available
      if (savedData.country_code) {
        this.setCountryCode(savedData.country_code);
      }
      if (savedData.code) {
        this.setCountryPhoneCode(savedData.code);
        this.selectedCode = savedData.code;
      }

      // Restore country selection in UI
      if (savedData.country_code && this.countries.length > 0) {
        const country = this.countries.find(c => c.code === savedData.country_code);
        if (country) {
          this.selectedCountry = country;
        }
      }
    }
  }

  /**
   * Auto-save form data to session storage
   */
  private autoSaveFormData(): void {
    const formData: FirstStepData = this.registerFormProvider.value;
    this.sessionStorageService.saveRegisterFirstStep(formData);
  }

  /**
   * Save form data manually (called on step transitions)
   */
  public saveFormData(): void {
    const formData: FirstStepData = this.registerFormProvider.value;
    this.sessionStorageService.saveRegisterFirstStep(formData);
  }

  /**
   * Clear saved form data
   */
  public clearSavedFormData(): void {
    this.sessionStorageService.clearRegisterState();
  }

  /**
   * Get saved form data
   */
  public getSavedFormData(): FirstStepData | null {
    return this.sessionStorageService.getRegisterFirstStep();
  }

  /**
   * Clear register state after successful registration
   */
  public clearRegisterStateAfterSuccess(): void {
    this.sessionStorageService.clearRegisterState();
  }

}
